// SF5 — Repeat Purchase, at product level.
//
// This is a ranking signal, not a count of repeat customers. No buyer identity
// is used, matched, stored or shown, so nothing here can say how many people
// came back — only which products behave like things people re-buy. The wording
// on this surface has to keep saying that, because a number in a table reads as
// a fact unless it is told otherwise.
//
// Three outcomes and they must not look alike: a measurable pattern, a product
// measured and showing none, and one with too little history to read at all.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderAnalytics.module.css';
import { getRepeatPurchase } from '../services/orderAnalyticsApi';

const BAND_CLASS = {
  strong: 'bandStrong',
  moderate: 'bandModerate',
  weak: 'bandWeak',
  none: 'bandNone',
  unmeasured: 'bandUnmeasured',
};

const BAND_LABEL = {
  strong: 'Strong', moderate: 'Moderate', weak: 'Weak',
  none: 'No pattern', unmeasured: 'Unmeasured',
};

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

const percent = (ratio) =>
  ratio === null || ratio === undefined ? <Blank /> : `${(ratio * 100).toFixed(0)}%`;

const Band = ({ row }) => (
  <span className={`${local.band} ${local[BAND_CLASS[row.band]] || ''}`} title={row.reason}>
    {BAND_LABEL[row.band] || row.band}
  </span>
);

const RepeatPurchase = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getRepeatPurchase(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];
  const bands = data?.bands || {};

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Repeat-purchase tendency</h2>
        {data && <span className={local.muted}>{count(data.products)} products</span>}
      </div>
      <p className={local.muted}>
        Which products behave like things people re-buy. This is a tendency read
        from the shape of demand — how many days a product sells on, and how
        often its orders take more than one. It is not a count of customers who
        came back, and no buyer identity is used anywhere.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load repeat purchase'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && data && (
        <>
          <div className={local.bandRow}>
            {['strong', 'moderate', 'weak', 'none', 'unmeasured'].map((band) => (
              bands[band] ? (
                <span key={band} className={`${local.band} ${local[BAND_CLASS[band]]}`}>
                  {BAND_LABEL[band]} {count(bands[band])}
                </span>
              ) : null
            ))}
          </div>

          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th>Tendency</th>
                  <th className={local.numeric}>Days sold on</th>
                  <th className={local.numeric}>Orders</th>
                  <th className={local.numeric}>Units</th>
                  <th className={local.numeric}>Orders taking more than one</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td className={local.emptyCell} colSpan={6}>No orders in this period.</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.asin}>
                    <td className={local.asinCell}>{row.asin}</td>
                    <td><Band row={row} /></td>
                    <td className={local.numeric}>
                      {row.active_days} of {row.window_days}
                    </td>
                    <td className={local.numeric}>{count(row.orders)}</td>
                    <td className={local.numeric}>{count(row.units)}</td>
                    <td className={local.numeric}>{percent(row.multi_unit_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.signal?.provisional && (
            <div className={local.provisional}>
              {data.signal.reason} Applied here: days-sold-on weighted{' '}
              {data.signal.continuity_weight}, multi-unit orders weighted{' '}
              {data.signal.multi_unit_weight}, and a product needs at least{' '}
              {data.signal.min_orders} orders across {data.signal.min_days} days
              before it is ranked at all.
            </div>
          )}
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RepeatPurchase;
